import React from "react";

import { DetailDisplay, Table } from "widgets";
import { reduceToFixedDecimals } from "utils/amountCalculation";
import { INR_SYMBOL } from "constants/config";

import styles from "./ReviewTransferOrder.module.scss";

const isPresentAddressPart = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (trimmed.toLowerCase() === "none") return false;
  }
  return true;
};

const formatCommaSeparatedAddress = (address) => {
  if (!address || typeof address !== "object") return "-";
  const parts = [
    address.recipientName,
    address.parentName,
    address.recipientPhoneNumber ?? address.maskedPhoneNo,
    address.recipientEmail,
    address.addressLine1,
    address.addressLine2,
    address.street,
    address.city,
    address.district,
    address.state,
    address.pincode ?? address.postalCode,
    address.country,
  ];
  const filtered = parts.filter(isPresentAddressPart);
  return filtered.length ? filtered.join(", ") : "-";
};

const getAdjustedPayment = (amountPaidInPreviousOrder, newOrderAmount) => {
  if (amountPaidInPreviousOrder > newOrderAmount) {
    return {
      adjustedPayment: newOrderAmount,
      remainingPaymentToRefund: amountPaidInPreviousOrder - newOrderAmount,
      remainingPaymentToCollect: 0,
    };
  } else if (amountPaidInPreviousOrder < newOrderAmount) {
    return {
      adjustedPayment: amountPaidInPreviousOrder,
      remainingPaymentToRefund: 0,
      remainingPaymentToCollect: newOrderAmount - amountPaidInPreviousOrder,
    };
  } else {
    return {
      adjustedPayment: newOrderAmount,
      remainingPaymentToRefund: 0,
      remainingPaymentToCollect: 0,
    };
  }
};

const ReviewTransferOrder = (props) => {
  const {
    transferOrderDetails,
    billingAddress,
    shippingAddress,
    useSameAddress,
    hasHardware,
    selectedPackageSubscriptions,
    discountDetails,
    newOrderAmount,
  } = props;

  const amountPaidInPreviousOrder =
    transferOrderDetails && transferOrderDetails.amountPaid;
  const {
    adjustedPayment,
    remainingPaymentToRefund,
    remainingPaymentToCollect,
  } = getAdjustedPayment(amountPaidInPreviousOrder, newOrderAmount);

  const newDiscountDetails = discountDetails
    ? `${discountDetails.coupon_code ? `Coupon Code: ${discountDetails.coupon_code}` : ""}
       ${discountDetails.discount_value ? (discountDetails.coupon_code ? " | " : "") + `Discount Value: ${discountDetails.is_percentage ? discountDetails.discount_value + "%" : "₹" + discountDetails.discount_value}` : ""}`.trim()
    : "-";

  const oldDiscountDetails = transferOrderDetails.coupon
    ? `${transferOrderDetails.coupon.code ? `Coupon Code: ${transferOrderDetails.coupon.code}` : ""}
       ${transferOrderDetails.coupon.value ? (transferOrderDetails.coupon.code ? " | " : "") + `Discount Value: ₹${transferOrderDetails.coupon.value}` : ""}`.trim()
    : "-";

  const newPackageNames = selectedPackageSubscriptions
    .map((pkg) => pkg.displayName)
    .join(", ");
  const previousPackageNames = transferOrderDetails.packages
    .map((pkg) => pkg.displayName)
    .join(", ");

  const previousBillingAddress =
    transferOrderDetails.addressDetails?.billingAddress;
  const previousShippingAddress =
    transferOrderDetails.addressDetails?.shippingAddress;

  const formattedNewBillingAddress =
    formatCommaSeparatedAddress(billingAddress);
  const formattedOldBillingAddress = formatCommaSeparatedAddress(
    previousBillingAddress,
  );

  const formattedNewShippingAddress = hasHardware
    ? useSameAddress
      ? formatCommaSeparatedAddress(billingAddress)
      : formatCommaSeparatedAddress(shippingAddress)
    : "-";
  const formattedOldShippingAddress = hasHardware
    ? formatCommaSeparatedAddress(previousShippingAddress)
    : "-";

  const columns = [
    { label: "Field", field: "field", align: "center" },
    { label: "Previous Value", field: "oldValue", align: "center" },
    { label: "New Value", field: "newValue", align: "center" },
  ];

  const rows = [
    {
      field: "Billing Address",
      oldValue: formattedOldBillingAddress,
      newValue: formattedNewBillingAddress,
    },
    ...(hasHardware
      ? [
          {
            field: "Shipping Address",
            oldValue: formattedOldShippingAddress,
            newValue: formattedNewShippingAddress,
          },
        ]
      : []),
    {
      field: "Package",
      oldValue: previousPackageNames,
      newValue: newPackageNames,
    },
    {
      field: "Discount Code",
      oldValue: oldDiscountDetails,
      newValue: newDiscountDetails,
    },
    {
      field: "Order Amount",
      oldValue: INR_SYMBOL + reduceToFixedDecimals(transferOrderDetails.amount),
      newValue: INR_SYMBOL + reduceToFixedDecimals(newOrderAmount),
    },
  ];

  return (
    <div className={styles.mainContainer}>
      <div className={styles.displayOrderId}>
        <span className={styles.title}>Previous Order Id :</span>
        <span className={styles.value}>{transferOrderDetails.orderId}</span>
      </div>
      <Table columns={columns} rows={rows} />
      <div className={styles.paymentDetails}>
        <DetailDisplay
          title="Adjusted Payment"
          value={INR_SYMBOL + reduceToFixedDecimals(adjustedPayment)}
        />
        <DetailDisplay
          title="Remaining payment to refund"
          value={INR_SYMBOL + reduceToFixedDecimals(remainingPaymentToRefund)}
        />
        <DetailDisplay
          title="Remaining payment to collect"
          value={INR_SYMBOL + reduceToFixedDecimals(remainingPaymentToCollect)}
        />
      </div>
    </div>
  );
};

export default ReviewTransferOrder;
